import React, { useState, useEffect, useRef } from "react";
import supabase from "../supabaseClient";
import { useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import Protegido from "../components/Protegido";
import { descargarPDFRecepcion } from "../utils/datosPDFRecepcion";
import { useNavigationState } from "../context/NavigationContext";
import { normalizarTexto } from "../utils/internacional";
import "../estilos/CrearDocumentoEstilo.css";

export default function BuscarRecepcion() {
  const { saveModuleState, getModuleState } = useNavigationState();
  const estadoGuardado = useRef(getModuleState("/buscar-recepcion")).current;
  const [cliente, setCliente] = useState(estadoGuardado?.cliente || "");
  const [clienteSeleccionado, setClienteSeleccionado] = useState(estadoGuardado?.clienteSeleccionado || null);
  const [listaClientes, setListaClientes] = useState([]);
  const [sugerencias, setSugerencias] = useState([]);
  const [inicio, setInicio] = useState(estadoGuardado?.inicio || "");
  const [fin, setFin] = useState(estadoGuardado?.fin || "");
  const [ordenes, setOrdenes] = useState([]);
  const [mostrar, setMostrar] = useState(estadoGuardado?.mostrar ?? false);
  const [pagina, setPagina] = useState(estadoGuardado?.pagina || 1);
const [totalPaginas, setTotalPaginas] = useState(1);
  const [totalResultados, setTotalResultados] = useState(0);
  const [loading, setLoading] = useState(estadoGuardado?.mostrar ?? false);
  const navigate = useNavigate();

  const limite = 20;

  // La lista de clientes se baja una sola vez y se filtra en el navegador. Asi
  // se pueden ignorar las tildes, cosa que la base de datos no sabe hacer sin
  // instalarle una extension.
  useEffect(() => {
    const cargarClientes = async () => {
      const { data } = await supabase.from("clientes").select("id, nombre, identificacion");
      if (data) setListaClientes(data);
    };
    cargarClientes();
  }, []);

  const escribirCliente = (valor) => {
    setCliente(valor);
    setClienteSeleccionado(null);
    if (!valor.trim()) {
      setSugerencias([]);
      return;
    }
    const buscado = normalizarTexto(valor);
    setSugerencias(
      listaClientes.filter((c) => normalizarTexto(c.nombre).includes(buscado)).slice(0, 30)
    );
  };

  const elegirCliente = (c) => {
    setCliente(c.nombre);
    setClienteSeleccionado({ id: c.id, nombre: c.nombre });
    setSugerencias([]);
  };

  const buscarRecepciones = async (paginaActual = 1) => {
    setLoading(true);
    const desde = (paginaActual - 1) * limite;
    const hasta = desde + limite - 1;

    const nombre = cliente.trim();

    // Si el cliente se eligio de la lista, se busca por su identificador: es
    // exacto y no confunde "Ana" con "Mariana". Si solo se escribio texto, se
    // resuelve aqui que clientes coinciden, ignorando tildes.
    let idsCliente = null;
    if (clienteSeleccionado?.id) {
      idsCliente = [clienteSeleccionado.id];
    } else if (nombre) {
      const buscado = normalizarTexto(nombre);
      const coinciden = listaClientes.filter((c) => normalizarTexto(c.nombre).includes(buscado));

      if (coinciden.length === 0) {
        setOrdenes([]);
        setPagina(1);
        setTotalResultados(0);
        setTotalPaginas(1);
        setMostrar(true);
        setLoading(false);
        return;
      }

      if (coinciden.length > 50) {
        setLoading(false);
        return Swal.fire(
          "Demasiados clientes",
          "Hay muchos clientes con ese nombre. Escribe un poco mas o elige uno de la lista.",
          "info"
        );
      }

      idsCliente = coinciden.map((c) => c.id);
    }

    // Los filtros tienen que ir DENTRO de la consulta. Filtrar en memoria solo
    // buscaba dentro de los 20 resultados de la pagina que ya estaba descargada.
    let consulta = supabase
      .from("ordenes_pedido")
      .select("*, clientes(nombre)", { count: "exact" })
      .eq("revisada", true);

    if (idsCliente) consulta = consulta.in("cliente_id", idsCliente);

    // Las fechas se comparan como texto, sin pasar por new Date(): asi no se
    // corren un dia (mismo criterio del Bloque J).
    if (inicio) consulta = consulta.gte("fecha_evento", inicio);
    if (fin) consulta = consulta.lte("fecha_evento", `${fin}T23:59:59`);

    const { data, error, count } = await consulta
      .order("fecha_evento", { ascending: false })
      .range(desde, hasta);

    setLoading(false);

    if (error) {
      console.error("Error al buscar recepciones:", error);
      return Swal.fire("Error", "No se pudieron cargar las recepciones.", "error");
    }

    setOrdenes(data || []);
    setPagina(paginaActual);
    setTotalResultados(count || 0);
    setTotalPaginas(Math.max(1, Math.ceil((count || 0) / limite)));
    setMostrar(true);
  };

  const limpiar = () => {
    setCliente("");
    setClienteSeleccionado(null);
    setSugerencias([]);
    setInicio("");
    setFin("");
    setOrdenes([]);
    setMostrar(false);
    setPagina(1);
    setTotalPaginas(1);
    setTotalResultados(0);
  };

  // Recordar la busqueda al volver con la flecha atras del navegador.
  // Se guardan solo los filtros y la pagina, no los resultados: al volver, la
  // lista se vuelve a consultar y muestra los datos frescos.
  useEffect(() => {
    saveModuleState("/buscar-recepcion", { cliente, clienteSeleccionado, inicio, fin, mostrar, pagina });
  }, [cliente, inicio, fin, mostrar, pagina, saveModuleState]);

  // Al entrar, si habia una busqueda activa, se repite sola.
  useEffect(() => {
    if (estadoGuardado?.mostrar) buscarRecepciones(estadoGuardado.pagina || 1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Descarga el PDF de una recepcion. Antes este boton abria /pdfrecepcion, una
  // ruta que no existe en App.js, asi que terminaba en la pagina de inicio.
  const abrirPDF = async (id) => {
    Swal.fire({
      title: "Generando PDF...",
      allowOutsideClick: false,
      didOpen: () => Swal.showLoading(),
    });
    try {
      await descargarPDFRecepcion(id);
      Swal.close();
    } catch (e) {
      console.error("Error generando el PDF de recepcion:", e);
      Swal.fire("Error", "No se pudo generar el PDF de esta recepcion.", "error");
    }
  };

  const inputStyle = {
    width: "100%", padding: "10px 12px",
    border: "1px solid var(--sw-borde)", borderRadius: 8,
    fontSize: 14, boxSizing: "border-box"
  };

  return (
    <Protegido>
      <div className="sw-pagina">
        <div className="sw-pagina-contenido" style={{ maxWidth: 900 }}>

          {/* ═══ HEADER ═══ */}
          <div className="sw-header">
            <h1 className="sw-header-titulo">🔍 Buscar Recepción</h1>
          </div>

          {/* ═══ FILTROS ═══ */}
          <div className="sw-card" style={{ marginBottom: 16 }}>
            <div className="sw-card-header sw-card-header-cyan">
              <h3 className="sw-card-titulo" style={{ color: "white", margin: 0 }}>🔎 Filtros de búsqueda</h3>
            </div>
            <div className="sw-card-body">
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
                <div style={{ gridColumn: "1 / -1" }}>
                  <label style={{ fontSize: 12, fontWeight: 600, color: "var(--sw-texto-secundario)", marginBottom: 4, display: "block" }}>
                    Cliente
                  </label>
                  <input
                    type="text"
                    value={cliente}
                    onChange={(e) => escribirCliente(e.target.value)}
                    placeholder="Buscar por nombre de cliente..."
                    style={inputStyle}
                  />
                  {sugerencias.length > 0 && (
                    <ul className="cd-sugerencias">
                      {sugerencias.map((c) => (
                        <li key={c.id} onClick={() => elegirCliente(c)}>
                          <strong>{c.nombre}</strong>
                          {c.identificacion ? ` — ${c.identificacion}` : ""}
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
                <div>
                  <label style={{ fontSize: 12, fontWeight: 600, color: "var(--sw-texto-secundario)", marginBottom: 4, display: "block" }}>
                    📅 Fecha evento (desde)
                  </label>
                  <input
                    type="date"
                    value={inicio}
                    onChange={(e) => setInicio(e.target.value)}
                    style={inputStyle}
                  />
                </div>
                <div>
                  <label style={{ fontSize: 12, fontWeight: 600, color: "var(--sw-texto-secundario)", marginBottom: 4, display: "block" }}>
                    📅 Fecha evento (hasta)
                  </label>
                  <input
                    type="date"
                    value={fin}
                    onChange={(e) => setFin(e.target.value)}
                    style={inputStyle}
                  />
                </div>
              </div>

              <div style={{ display: "flex", gap: 10, marginTop: 16, flexWrap: "wrap" }}>
                <button className="sw-btn sw-btn-primario" style={{ flex: 1, minWidth: 120 }} onClick={() => buscarRecepciones(1)}>
                  🔍 Buscar
                </button>
                <button className="sw-btn sw-btn-secundario" style={{ flex: 1, minWidth: 120 }} onClick={limpiar}>
                  🧹 Limpiar
                </button>
                {mostrar && (
                  <button className="sw-btn sw-btn-secundario" style={{ flex: 1, minWidth: 120 }} onClick={() => setMostrar(false)}>
                    👁️ Ocultar
                  </button>
                )}
              </div>
            </div>
          </div>

          {/* ═══ RESULTADOS ═══ */}
          {mostrar && (
            <div className="sw-card">
              <div className="sw-card-header">
                <h3 className="sw-card-titulo">
                  📋 Resultados — {totalResultados} recepción{totalResultados !== 1 ? "es" : ""} · Página {pagina} de {totalPaginas}
                </h3>
              </div>
              <div className="sw-card-body" style={{ padding: 0 }}>
                {loading ? (
                  <div style={{ padding: 40, textAlign: "center", color: "var(--sw-texto-terciario)" }}>
                    Buscando recepciones...
                  </div>
                ) : ordenes.length === 0 ? (
                  <div style={{ padding: 40, textAlign: "center" }}>
                    <div style={{ fontSize: 40, marginBottom: 8 }}>📭</div>
                    <div style={{ fontSize: 14, color: "var(--sw-texto-terciario)" }}>
                      No se encontraron recepciones con esos filtros
                    </div>
                  </div>
                ) : (
                  ordenes.map((o) => (
                    <div key={o.id} style={{
                      padding: "14px 16px",
                      borderBottom: "1px solid #f3f4f6",
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      gap: 10,
                      transition: "background 0.15s",
                    }}
                      onMouseEnter={(e) => e.currentTarget.style.background = "#f9fafb"}
                      onMouseLeave={(e) => e.currentTarget.style.background = "transparent"}
                    >
                      {/* Info */}
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                          <span style={{ fontWeight: 600, fontSize: 14, color: "var(--sw-texto)" }}>
                            {o.clientes?.nombre || "Sin cliente"}
                          </span>
                          {o.numero && (
                            <span style={{
                              fontSize: 11, padding: "2px 8px", borderRadius: 20,
                              background: "var(--sw-cyan-muy-claro)", color: "var(--sw-azul)", fontWeight: 500
                            }}>
                              {o.numero}
                            </span>
                          )}
                        </div>
                        <div style={{ fontSize: 12, color: "var(--sw-texto-terciario)", marginTop: 3, display: "flex", flexWrap: "wrap", gap: "4px 12px" }}>
                          {o.fecha_evento && <span>📅 Evento: {o.fecha_evento.split("T")[0]}</span>}
                          {o.total && (
                            <span style={{ fontWeight: 600, color: "var(--sw-verde-oscuro)" }}>
                              💰 ${Number(o.total).toLocaleString("es-CO")}
                            </span>
                          )}
                        </div>
                      </div>

                      {/* Acciones */}
                      <div style={{ display: "flex", gap: 6, flexShrink: 0, flexWrap: "wrap" }}>
                        <button
                          className="sw-btn sw-btn-secundario"
                          style={{ padding: "5px 10px", fontSize: 12 }}
                          onClick={() => navigate(`/recepcion?id=${o.id}`)}
                        >
                          ✏️ Editar
                        </button>
                        <button
                          className="sw-btn sw-btn-secundario"
                          style={{ padding: "5px 10px", fontSize: 12 }}
                          onClick={() => abrirPDF(o.id)}
                        >
                          📄 PDF
                        </button>
                      
                      </div>
                    </div>
                  ))
                )}

                {/* Paginación */}
                {ordenes.length > 0 && totalPaginas > 1 && (
                  <div style={{
                    display: "flex", justifyContent: "center", gap: 12,
                    padding: "14px 16px", borderTop: "1px solid #f3f4f6"
                  }}>
                    <button
                      className="sw-btn sw-btn-secundario"
                      disabled={pagina <= 1}
                      onClick={() => buscarRecepciones(pagina - 1)}
                      style={{ opacity: pagina <= 1 ? 0.5 : 1 }}
                    >
                      ⬅️ Anterior
                    </button>
                    <span style={{ display: "flex", alignItems: "center", fontSize: 13, color: "var(--sw-texto-secundario)" }}>
                      {pagina} / {totalPaginas}
                    </span>
                    <button
                      className="sw-btn sw-btn-secundario"
                      disabled={pagina >= totalPaginas}
                      onClick={() => buscarRecepciones(pagina + 1)}
                      style={{ opacity: pagina >= totalPaginas ? 0.5 : 1 }}
                    >
                      Siguiente ➡️
                    </button>
                  </div>
                )}
              </div>
            </div>
          )}

        </div>
      </div>
    </Protegido>
  );
}